<script setup lang="ts">
import { parseErrors } from "@/assets/js/parseErrors";
import { useToastStore } from "@/store/toast";
import api from "@/api";

// const props = defineProps({
// 	day: {
// 		type: Object,
// 		default: () => ({}),
// 	},
// 	classNameFilter: Function,
// 	getProp: Function,
// 	getShifts: Function,
// 	tempId: {
// 		type: [Number, null],
// 		default: null,
// 		validator: (prop: any) => typeof prop === "number" || prop === null,
// 	},
// });

const props = withDefaults(
	defineProps<{
		day: Record<string, any>;
		classNameFilter: (arg: any, day: number) => any;
		getProp: (arg: string, day: any) => any;
		getShifts: (arg: string, day: any) => any;
		tempId: number | null;
	}>(),
	{
		day: () => ({}),
		classNameFilter: () => {},
		getProp: () => {},
		getShifts: () => {},
		tempId: null,
	},
);

const emit = defineEmits(["close-modal", "confirm"]);

const enabledDay = ref<boolean | null>(null);
const enabledNight = ref<boolean | null>(null);
const daySwitchDisabled = ref(false);
const nightSwitchDisabled = ref(false);
const oldAvailabilityValues = ref({
	isAvailableDay: null,
	isAvailableNight: null,
});

const getDayObject = () => {
	const root = Object.keys(props.day)[0];
	return props.day[root];
};

const changeSelectedDay = (prop: string, value: boolean) => {
	const dayObject = getDayObject();
	dayObject[prop] = value ? 1 : 0;
};

const changeSelectedDayBetter = (prop: string, value: boolean | null) => {
	const dayObject = getDayObject();
	if (value === true) {
		dayObject[prop] = 1;
	} else if (value === false) {
		dayObject[prop] = 0;
	} else {
		dayObject[prop] = null;
	}
};

const onEnabledDayChange = (value: boolean) => {
	if (daySwitchDisabled.value) return;
	enabledDay.value = value;
	changeSelectedDay("isAvailableDay", enabledDay.value);
};

const onEnabledNightChange = (value: boolean) => {
	if (nightSwitchDisabled.value) return;
	enabledNight.value = value;
	changeSelectedDay("isAvailableNight", enabledNight.value);
};

const callModalClose = () => {
	const dayObject = getDayObject();
	Object.assign(dayObject, oldAvailabilityValues.value);
	emit("close-modal");
};

const callModalSubmit = async () => {
	const dateString = Object.keys(props.day)[0];

	const params = {
		tempId: props.tempId,
		date: dateString,
		is_available_day: enabledDay.value === null ? null : enabledDay.value,
		is_available_night: enabledNight.value === null ? null : enabledNight.value,
	};

	try {
		const response = await api.saveAvailability(params);
		console.log("Submitted availability data response", response.data);
		emit("confirm");
		emit("close-modal");
	} catch (err) {
		console.warn((err as Error).message);
		const errs = parseErrors(err);
		if (errs) {
			useToastStore().openToastError(errs);
		}
	}
};

const callModalReset = () => {
	changeSelectedDayBetter("isAvailableDay", null);
	changeSelectedDayBetter("isAvailableNight", null);
	enabledDay.value = null;
	enabledNight.value = null;
};

onMounted(() => {
	const isAvailableDay = props.getProp("isAvailableDay", props.day);
	const isAvailableNight = props.getProp("isAvailableNight", props.day);

	oldAvailabilityValues.value = { isAvailableDay, isAvailableNight };

	const shifts = props.getProp("shifts", props.day);
	const dailyShifts = props.getShifts(shifts, 1);
	const nightlyShifts = props.getShifts(shifts, 2);

	daySwitchDisabled.value = dailyShifts.length > 0;
	nightSwitchDisabled.value = nightlyShifts.length > 0;

	if (isAvailableDay === 1) {
		enabledDay.value = true;
	} else if (isAvailableDay === 0) {
		enabledDay.value = false;
	}

	if (isAvailableNight === 1) {
		enabledNight.value = true;
	} else if (isAvailableNight === 0) {
		enabledNight.value = false;
	}
});
</script>

<template>
	<div class="dialog-container">
		<div class="availability-content">
			<div class="calendar__cell">
				<div class="visual">
					<div
						class="visual__day"
						:class="classNameFilter(day, 1)"
					>
						<p>
							{{
								getShifts(getProp("shifts", day), 1)?.length > 0
									? getShifts(getProp("shifts", day), 1).length
									: ""
							}}
						</p>
					</div>
					<div
						class="visual__night"
						:class="classNameFilter(day, 2)"
					>
						<p>
							{{
								getShifts(getProp("shifts", day), 2)?.length > 0
									? getShifts(getProp("shifts", day), 2).length
									: ""
							}}
						</p>
					</div>
				</div>
				<div class="data">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="25"
						height="25"
						viewBox="0 0 25 25"
					>
						<g
							fill="#006AD4"
							fill-rule="evenodd"
						>
							<path
								d="M12.4009153 6.12445652C8.97556064 6.12445652 6.18892449 8.8826087 6.18892449 12.2722826 6.18892449 15.6619565 8.97556064 18.4201087 12.4009153 18.4201087 15.8257208 18.4201087 18.6129062 15.6625 18.6129062 12.2722826 18.6129062 8.88206522 15.8257208 6.12445652 12.4009153 6.12445652zM12.4009153 4.3173913C11.7314416 4.3173913 11.188833 3.78043478 11.188833 3.11847826L11.188833 1.19945652C11.188833.536956522 11.7314416 0 12.4009153 0 13.070389 0 13.6129977.536956522 13.6129977 1.19945652L13.6129977 3.11847826C13.6129977 3.78043478 13.0698398 4.3173913 12.4009153 4.3173913zM12.4009153 20.2255435C11.7314416 20.2255435 11.188833 20.7625 11.188833 21.425L11.188833 23.3434783C11.188833 24.0065217 11.7314416 24.5434783 12.4009153 24.5434783 13.070389 24.5434783 13.6129977 24.0065217 13.6129977 23.3434783L13.6129977 21.425C13.6129977 20.7625 13.0698398 20.2255435 12.4009153 20.2255435zM18.0840275 6.64728261C17.611167 6.17880435 17.611167 5.41956522 18.0840275 4.95108696L19.4553776 3.59402174C19.928238 3.12608696 20.6960183 3.12608696 21.1694279 3.59402174 21.6428375 4.0625 21.6428375 4.82228261 21.1694279 5.29021739L19.7980778 6.64728261C19.3252174 7.11576087 18.5579863 7.11576087 18.0840275 6.64728261zM6.717254 17.8967391C6.24384439 17.4277174 5.47661327 17.4277174 5.00320366 17.8967391L3.63185355 19.2532609C3.15899314 19.7211957 3.15844394 20.4815217 3.63185355 20.9494565 4.10526316 21.4173913 4.87304348 21.4173913 5.34590389 20.9494565L6.717254 19.5918478C7.19066362 19.123913 7.19066362 18.3641304 6.717254 17.8967391zM20.4384439 12.2717391C20.4384439 11.6092391 20.9810526 11.0722826 21.6505263 11.0722826L23.5897483 11.0722826C24.259222 11.0722826 24.8018307 11.6092391 24.8018307 12.2717391 24.8018307 12.9342391 24.259222 13.4706522 23.5897483 13.4706522L21.6505263 13.4706522C20.9810526 13.4706522 20.4384439 12.9342391 20.4384439 12.2717391zM4.36283753 12.2717391C4.36283753 11.6092391 3.82022883 11.0722826 3.15075515 11.0722826L1.21208238 11.0722826C.542608696 11.0722826 0 11.6092391 0 12.2717391 0 12.9342391.542608696 13.4706522 1.21208238 13.4706522L3.15130435 13.4706522C3.82022883 13.4706522 4.36283753 12.9342391 4.36283753 12.2717391zM18.0840275 17.8967391C18.5574371 17.4288043 19.3252174 17.4288043 19.7980778 17.8967391L21.1694279 19.2538043C21.6428375 19.7211957 21.6428375 20.4815217 21.1694279 20.9494565 20.6960183 21.4173913 19.9287872 21.4173913 19.4553776 20.9494565L18.0840275 19.5923913C17.6106178 19.123913 17.6106178 18.3646739 18.0840275 17.8967391zM6.717254 6.64728261C7.19066362 6.17880435 7.19066362 5.41956522 6.717254 4.95108696L5.34590389 3.59456522C4.87249428 3.12608696 4.10526316 3.12608696 3.63185355 3.59456522 3.15844394 4.0625 3.15844394 4.82228261 3.63185355 5.29021739L5.00320366 6.64728261C5.47661327 7.11630435 6.24384439 7.11630435 6.717254 6.64728261z"
							/>
						</g>
					</svg>
					<p>{{ getProp("root", day).slice(-2) }}</p>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="15"
						height="16"
						viewBox="0 0 15 16"
					>
						<path
							fill="#006AD4"
							fill-rule="evenodd"
							d="M12.594799,1.37507025 C12.045008,0.81778038 11.4344795,0.359828481 10.7850361,0 C12.4337153,3.0568481 11.9818738,6.96778544 9.42837624,9.55581456 C6.8748787,12.1438437 3.01606117,12.6018595 -9.61097868e-12,10.9308848 C0.355027359,11.5891108 0.806868894,12.2078956 1.35672296,12.7651215 C4.46001149,15.9103766 9.49151044,15.9103766 12.594862,12.7651215 C15.6981506,9.61986646 15.6981506,4.52032532 12.594799,1.37507025 Z"
							transform="matrix(-1 0 0 1 15 0)"
						/>
					</svg>
				</div>
			</div>
			<div class="switches">
				<div class="group">
					<label>Day</label>
					<tri-state-switch
						:value="enabledDay"
						:class="[
							{ empty: enabledDay === null },
							{ disabled: daySwitchDisabled },
						]"
						@input="onEnabledDayChange"
					></tri-state-switch>
				</div>
				<div class="group">
					<label>Night</label>
					<tri-state-switch
						:value="enabledNight"
						:class="[
							{ empty: enabledNight === null },
							{ disabled: nightSwitchDisabled },
						]"
						@input="onEnabledNightChange"
					></tri-state-switch>
				</div>
			</div>
		</div>
		<div class="buttons-centered">
			<button
				class="button alt is-generic-app-blue"
				@click.prevent="callModalSubmit"
			>
				Submit
			</button>
			<button
				class="button"
				@click.prevent="callModalReset"
			>
				Reset
			</button>
			<button
				class="button is-outlined"
				@click.prevent="callModalClose"
			>
				Cancel
			</button>
		</div>
	</div>
</template>
<style lang="scss" scoped>
$calendar-neutral: #ebeff1;
$calendar-font-color: #2d5764;
$calendar-pale: #f2f7fa;
$calendar-blue: #0077e5;
$calendar-red: #e41616;
$calendar-light-green: #16c03d;
$calendar-dark-green: #14b338;
$calendar-light-grey: #c4cfd4;
$calendar-dark-grey: #b5c1c7;
$calendar-color-data: #4b5e6f;
.dialog-container {
	box-sizing: border-box;
	padding: 0 20px 20px;
	min-width: 335px;
}
.calendar__cell {
	background-color: $calendar-neutral;
	height: 110px;
	margin-bottom: 5px;
	display: flex;
	width: 75%;

	&.is-selected-cell {
		outline: 5px solid #294959;
	}

	&.has-no-gap {
		height: 40px;
		justify-content: center;
		align-items: center;
		font-weight: 900;
		color: $calendar-font-color;
	}

	.visual,
	.data {
		flex: 1;
		font-size: smaller;
	}

	.visual {
		display: flex;
		flex-direction: column;
	}

	.visual__day,
	.visual__night {
		background-color: $calendar-light-grey;
		height: 55px;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 20px;
		color: $white;
		opacity: 0.6; // TODO just testing
		transition: background-color 1s;
	}

	.visual__night {
		background-color: $calendar-dark-grey;
	}

	.visual__day--available {
		background-color: $calendar-light-green;
	}

	.visual__night--available {
		background-color: color.adjust($calendar-light-green, $lightness: -11%);
	}

	.visual__day--booked {
		background-color: $calendar-blue;
	}

	.visual__night--booked {
		background-color: color.adjust($calendar-blue, $lightness: -11%);
	}

	.visual__day--action_needed {
		background-color: $calendar-red;
	}

	.visual__night--action_needed {
		background-color: color.adjust($calendar-red, $lightness: -11%);
	}

	.visual__day--all,
	.visual__night--all {
		color: color.adjust($calendar-color-data, $lightness: -20%);
	}

	.data {
		display: flex;
		flex-direction: column;
		justify-content: space-around;
		align-items: center;
		color: $calendar-color-data;
		font-size: 25px;
		font-weight: 500;
		line-height: 30px;

		svg {
			margin-right: auto;
		}
		svg:first-child {
			margin-left: 6px;
			width: 20%;
			height: auto;
		}
		svg:last-child {
			margin-left: 10px;
			width: 18%;
			height: auto;
		}
	}
}

.availability-content {
	height: 130px;
	display: flex;
	padding-top: 17px;

	.calendar__cell {
		width: 100% !important;
	}
}

.switches {
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 15px;
	> label {
		flex: 1;
	}

	.group {
		display: flex;
		align-items: center;
		justify-content: space-between;

		label {
			text-align: right;
			width: 40px;
			margin-right: 10px;
		}
	}
}
</style>

<style lang="scss">
$temp-calendar-available: #3999fa;
$temp-calendar-unavailable: #f7a53f;

.availability-content {
	.vue-switcher--bold {
		div {
			top: 0;
		}
	}

	.empty.tri-state-switch-theme--bulma {
		div {
			&::after {
				left: 38px;
			}
		}
	}

	.tri-state-switch {
		&.disabled {
			cursor: not-allowed !important;

			div {
				cursor: not-allowed !important;

				&::after {
					cursor: not-allowed !important;
				}
			}
		}
	}

	.tri-state-switch-theme--bulma.tri-state-switch-color--default.tri-state-switch--unchecked
		div {
		background: #c0bfbf;
	}
	.tri-state-switch-theme--bulma.tri-state-switch-color--default:not(.empty)
		div {
		background-color: $temp-calendar-available !important;
	}
	.tri-state-switch-theme--bulma.tri-state-switch-color--default.tri-state-switch--unchecked:not(
			.empty
		)
		div {
		background-color: $temp-calendar-unavailable !important;
	}
}
</style>
